import { useI18n } from '../../../i18n.js';
import { Icon } from '../../Icon.js';
import { VoiceChatBar } from '../../VoiceChatBar.js';

interface Big2ControlsProps {
  isMyTurn: boolean;
  mustPlay: boolean;
  isPaused: boolean;
  cannotBeat: boolean;
  playError: string | null;
  onPass: () => void;
  onPlay: () => void;
  isFinished?: boolean;
  isHost?: boolean;
  matchOver?: boolean;
  onPlayAgain?: () => void;
  onRestartMatch?: () => void;
  onReturnToLobby?: () => void;
}

export function Big2Controls({
  isMyTurn,
  mustPlay,
  isPaused,
  cannotBeat,
  playError,
  onPass,
  onPlay,
  isFinished,
  isHost,
  matchOver,
  onPlayAgain,
  onRestartMatch,
  onReturnToLobby,
}: Big2ControlsProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';

  if (isFinished) {
    return (
      <div className="player-controls">
        <div className="player-controls-secondary">
          <VoiceChatBar compact />
        </div>

        <div className="player-controls-primary">
          {isHost ? (
            <>
              {!matchOver && onPlayAgain && (
                <button
                  type="button"
                  className="play-action is-suggested"
                  onClick={onPlayAgain}
                  title={isMn ? 'Дараагийн үе эхлүүлэх' : 'Start Next Round'}
                >
                  <Icon name="play" />
                  <span>{isMn ? 'Дараагийн үе' : 'Next Round'}</span>
                </button>
              )}
              {onRestartMatch && (
                <button
                  type="button"
                  className={`play-action${matchOver ? ' is-suggested' : ''}`}
                  onClick={onRestartMatch}
                  style={!matchOver ? { background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.25)' } : undefined}
                  title={isMn ? 'Шинэ тэмцээн эхлүүлэх (Оноог тэглэнэ)' : 'Start New Match (Reset points)'}
                >
                  <Icon name="rotate-ccw" />
                  <span>{isMn ? 'Шинэ тэмцээн' : 'New Match'}</span>
                </button>
              )}
              {onReturnToLobby && (
                <button
                  type="button"
                  className="pass-action"
                  onClick={onReturnToLobby}
                  title={isMn ? 'Лобби руу буцах' : 'Return to Lobby'}
                >
                  <Icon name="log-out" />
                  <span>{isMn ? 'Лобби' : 'Lobby'}</span>
                </button>
              )}
            </>
          ) : (
            <>
              <div
                style={{
                  color: '#fde68a',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  padding: '6px 12px',
                  background: 'rgba(0, 0, 0, 0.35)',
                  borderRadius: 8,
                }}
              >
                {isMn ? 'Эзэн эхлүүлэхийг хүлээж байна...' : 'Waiting for host to start...'}
              </div>
              {onReturnToLobby && (
                <button
                  type="button"
                  className="pass-action"
                  onClick={onReturnToLobby}
                  title={isMn ? 'Лобби руу буцах' : 'Return to Lobby'}
                >
                  <Icon name="log-out" />
                  <span>{isMn ? 'Лобби' : 'Lobby'}</span>
                </button>
              )}
            </>
          )}
        </div>

        <div className="player-controls-spacer" />
      </div>
    );
  }

  return (
    <div className="player-controls">
      <div className="player-controls-secondary">
        <VoiceChatBar compact />
      </div>

      <div className="player-controls-primary">
        <button
          className={`pass-action${cannotBeat ? ' is-suggested' : ''}`}
          onClick={onPass}
          disabled={!isMyTurn || mustPlay || isPaused}
          aria-label={cannotBeat ? t.passSuggested : t.passButton}
        >
          <Icon name="skip-forward" />
          <span>{t.passButton}</span>
        </button>

        <button
          className={`play-action${cannotBeat ? ' is-dimmed' : ''}`}
          onClick={onPlay}
          disabled={!isMyTurn || !!playError || isPaused}
          title={playError ?? t.playButton}
        >
          <Icon name="arrow-up" />
          <span>{t.playButton}</span>
        </button>
      </div>

      <div className="player-controls-spacer" />
    </div>
  );
}
